import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Trophy, Medal, Users } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { NoahMascot } from './NoahMascot';
import { InfoTip } from './InfoTip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ExplorerLeaderboard = () => {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tour/leaderboard`);
      setData(res.data);
    } catch { /* silencioso */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const rows = data.leaderboard || [];

  return (
    <Card className="bg-card/80 backdrop-blur border-white/10" data-testid="explorer-leaderboard">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-brand-copper" />
          <h3 className="font-semibold text-foreground">Ranking de exploradores</h3>
          <InfoTip
            title="Ranking de exploradores"
            text="Quién de tu equipo terminó el tour guiado de Noah. El primero de cada mes se lleva la corona."
            source="Progreso del tour de cada usuario (colección tour_progress)"
          />
          <Badge variant="outline" className="ml-auto text-[11px] gap-1">
            <Users className="w-3 h-3" /> {data.finished} completados · {data.in_progress} en curso
          </Badge>
        </div>

        {rows.length === 0 ? (
          <div className="flex items-center gap-3 py-2">
            <NoahMascot mood="search" size={72} />
            <p className="text-sm text-muted-foreground">
              Nadie ha terminado el tour todavía. Sé el primero del mes y gana la corona.
            </p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {rows.slice(0, 8).map((r, idx) => (
              <div key={`${r.user_id}-${idx}`}
                data-testid={`explorer-row-${r.user_id}`}
                className={`flex items-center gap-3 px-3 py-2 rounded-xl ${
                  r.champion ? 'bg-brand-butter text-brand-deep' : r.is_you ? 'bg-primary/10' : 'bg-muted/30'
                }`}>
                <span className={`text-xs font-bold w-5 ${r.champion ? 'text-brand-deep' : 'text-muted-foreground'}`}>
                  {idx + 1}
                </span>
                {r.champion ? <Trophy className="w-4 h-4 shrink-0" /> : <Medal className="w-4 h-4 shrink-0 text-muted-foreground" />}
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-medium truncate ${r.champion ? 'text-brand-deep' : 'text-foreground'}`}>
                    {r.name}{r.is_you && ' (tú)'}
                  </p>
                  <p className={`text-[11px] truncate ${r.champion ? 'text-brand-deep/70' : 'text-muted-foreground'}`}>
                    {r.variant === 'plus' ? 'Tour Plus' : 'Tour de bienvenida'} ·{' '}
                    {r.finished_at ? new Date(r.finished_at).toLocaleDateString('es') : ''}
                  </p>
                </div>
                {r.champion && (
                  <Badge className="bg-brand-deep text-brand-butter border-none text-[10px] shrink-0"
                    data-testid="explorer-champion">
                    1º del mes
                  </Badge>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ExplorerLeaderboard;
